import { userAtom } from "@/Atoms";
import Layout from "@/components/elements/Layout";
import SubNavigation from "@/components/elements/SubNavigation";
import LockedContent from "@/components/LockedContent";
import ShareButtons from "@/components/parts/ShareButtons";
import ShareButtonsVertical from "@/components/parts/ShareButtonsVertical";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import Schema from "@/components/schema/Schema";
import { useGetCurrentUrl } from "@/hooks";
import useAuth from "@/hooks/auth";
import {
  getContentDetailBySlug,
  getRelatedContentName,
  trackContentAccessCounting,
} from "@/services/api";
import { cleanHTML, CONTENT_TYPES } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import { fitIn } from "@/utils/imageHandler";
import isPersonName from "@/utils/isPersonName";
import { useAtom } from "jotai";
import Head from "next/head";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ImageLogo from "../../assets/images/nav-logo-vertical.svg";
import { Button } from "@/components/ui/button";
import { BookOpen } from "@phosphor-icons/react/dist/ssr";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import {
  LinkedinLogo,
  TwitterLogo,
  FacebookLogo,
  InstagramLogo,
} from "@phosphor-icons/react";
import AuthorContent from "@/components/AuthorContent";
import RelatedReport from "@/components/RelatedReport";

function InvestorDetail(props) {
  const router = useRouter();
  const { user } = useAuth();
  const [reach_limit, setReachLimit] = useState(false);
  const [userState, setUserState] = useAtom(userAtom);
  const [expandedAuthors, setExpandedAuthors] = useState({});

  //   const [related_items, isLoading] = useGetRelatedContents(
  //     CONTENT_TYPES.NEWS,
  //     props.opinion.uuid
  //   );

  const [opinion, setOpinions] = useState(props.opinions ?? {});
  //   const [otherReports, otherReportLoading] = useGetContentsByContentType(
  //     CONTENT_TYPES.SPECIAL_REPORTS
  //   );

  const loadOpinions = async () => {
    try {
      const response = await getContentDetailBySlug(router.query.slug);
      setOpinions(response.data.data);
    } catch {
      // TODO Handle error
    }
  };

  const fetchMeteredContent = async () => {
    if (
      userState?.roles === "admin-team" ||
      userState?.roles === "read-access"
    ) {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    loadOpinions();
    fetchMeteredContent();
  }, [router.query.slug, userState]);

  const fullUrl = useGetCurrentUrl();

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      mainEntityOfPage: {
        "@type": "WebPage",
        id: fullUrl,
      },
      headline: opinion.meta_title,
      image: [opinion.thumbnail_url],
      datePublished: opinion.publish_date,
      dateModified: opinion.publish_date,
      author: {
        "@type": isPersonName(opinion.author.name) ? "Person" : "Organization",
        name: isPersonName(opinion.author.name)
          ? opinion.author.name
          : "Lucidity Insights",
      },
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
      description: opinion.meta_desc,
    },
  ];

  // const structuredData = [
  //   {
  //     "@context": "https://schema.org",
  //     "@type": "Article",
  //     mainEntityOfPage: {
  //       "@type": "WebPage",
  //       "@id": fullUrl,
  //     },
  //     headline: opinion.meta_title,
  //     description: opinion.meta_desc,
  //     image: opinion.thumbnail_url,
  //     //  author: {
  //     //    "@type": "Person",
  //     //    name: "Erica",
  //     //    url: "https://lucidityinsights.com/author/erica",
  //     //  },
  //     publisher: {
  //       "@type": "Organization",
  //       name: "Lucidity Insights",
  //       logo: {
  //         "@type": "ImageObject",
  //         url: ImageLogo.src,
  //       },
  //     },
  //     datePublished: opinion.publish_date,
  //     dateModified: opinion.publish_date,
  //   },
  //   {
  //     "@context": "https://schema.org/",
  //     "@type": "BreadcrumbList",
  //     itemListElement: [
  //       {
  //         "@type": "ListItem",
  //         position: 1,
  //         name: "Home",
  //         item: `${process.env.BASE_URL}`,
  //       },
  //       {
  //         "@type": "ListItem",
  //         position: 2,
  //         name: "Article",
  //         item: `${process.env.BASE_URL}articles`,
  //       },
  //       {
  //         "@type": "ListItem",
  //         position: 3,
  //         name: opinion.title,
  //         item: `${process.env.BASE_URL}articles/${router.query.slug}`,
  //       },
  //     ],
  //   },
  // ];

  const toggleAuthorDescription = (authorId) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorId]: !prev[authorId],
    }));
  };

  return (
    <div>
      <Head>
        <meta
          property='article:published_time'
          content={new Date(opinion.publish_date).toISOString()}
        />
        <meta
          property='article:modified_time'
          content={new Date(opinion.publish_date).toISOString()}
        />
        <meta property='article:section' content='Article Section' />
      </Head>
      <Schema data={structuredData} />
      <Layout
        title={opinion.title}
        meta_title={opinion.meta_title}
        description={opinion.meta_desc}
        meta_image={opinion.cover_image_url}
      >
        <SubNavigation title={opinion.title}></SubNavigation>
        <div className='px-4 mx-auto max-w-7xl lg:flex'>
          <div className='flex items-start flex-1 pt-6 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:mr-8 lg:border-r lg:space-x-8 2xl:space-x-12 border-white/5'>
            <div className='sticky hidden pt-2 lg:block top-40'>
              <ShareButtonsVertical
                title={opinion.title}
                description={opinion.meta_desc}
                url={fullUrl}
              />
            </div>
            <div className='relative w-full space-y-10 lg:space-y-16 max-w-screen-2xl'>
              <article className='relative mx-auto'>
                <h1 className='text-3xl leading-[1.1] font-bold tracking-tight lg:text-5xl font-display'>
                  {opinion.title}
                </h1>

                <div className='flex items-center justify-between gap-4 mt-3'>
                  <p className='flex items-center text-sm'>
                    <span className='opacity-40'>
                      {`${formatDate(opinion.publish_date, "DD MMMM YYYY")}`}
                    </span>
                    <span className='ml-2 opacity-40'>&bull;</span>
                    <ContentWishlistComponent item={opinion} />
                  </p>
                  <span className='flex-1 h-px bg-white/10'></span>
                  <div className='lg:hidden'>
                    <ShareButtons
                      title={opinion.title}
                      description={opinion.meta_desc}
                      url={fullUrl}
                    />
                  </div>
                </div>

                <div className='pt-3 pb-6 text-sm text-white/70'>
                  By{" "}
                  {opinion.authors.map((author, index) => (
                    <Link
                      key={index}
                      href={`/author/${author.slug}`}
                      className='no-underline'
                    >
                      <span className='underline'>{author.name}</span>
                      {opinion.authors.length > 2 &&
                        index < opinion.authors.length - 2 &&
                        ", "}
                      {opinion.authors.length > 1 &&
                        index === opinion.authors.length - 2 &&
                        " and "}
                    </Link>
                  ))}{" "}
                </div>

                <div className='relative w-full h-auto'>
                  <Image
                    src={fitIn(opinion.cover_image_url, 1000)}
                    alt={opinion.cover_image_alt}
                    width={640}
                    height={320}
                    layout={"responsive"}
                    objectFit={"cover"}
                    className='rounded-[15px]'
                  />
                </div>

                <div className='pt-6 mx-auto prose lg:pt-8 content prose-invert prose-img:rounded-lg'>
                  {reach_limit
                    ? cleanHTML(opinion.description.slice(0, 850))
                    : cleanHTML(opinion.description)}
                </div>
                {reach_limit && (
                  <div className='absolute bottom-0 w-full pt-40 bg-gradient-to-b from-background/0 via-background/90 to-background'>
                    <LockedContent
                      title="You've Reached Your Monthly Limit"
                      description='You are currently on our free plan. Upgrade to enjoy unlimited access.'
                      isLogin={user?.id}
                      blur={false}
                    />
                  </div>
                )}
              </article>
              <AuthorContent data={opinion.authors} />
            </div>
          </div>

          <div className='lg:pt-10 lg:max-w-[340px] text-white lg:pb-16 space-y-8 lg:space-y-10'>
            <RelatedReport
              connectedWith={opinion.connected_with}
              relatedItems={props.related_items}
            />
            <RelatedContentSidebar
              title='Related Videos & Podcasts'
              currentContentId={opinion.uuid}
              contentType={CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES}
            />
            <RelatedContentSidebar
              title='Did you check these out?'
              currentContentId={opinion.uuid}
            />
          </div>
        </div>

        {/* <div className='mb-14 lg:mb-24'>
          {otherReports.length > 0 && (
            <SpecialReportsSlider
              reports={otherReports.filter(
                (other) => other.uuid !== opinion.connected_with.uuid
              )}
              isLoading={otherReportLoading}
              title='Check Out Some of Our Other Reports'
              decoration
            />
          )}
        </div> */}
      </Layout>
    </div>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.OPINIONS,
      },
    });

    const responseRelated = await getRelatedContentName(
      CONTENT_TYPES.SPECIAL_REPORTS,
      response.data.data.uuid
    );

    return {
      props: {
        opinions: response.data.data,
        related_items: responseRelated.data?.data || [],
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default InvestorDetail;
